import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../core/contexts/AuthContext';
import { useTenant } from '../../core/contexts/TenantContext';
import { useActiveProject, type ActiveProject } from '../../core/contexts/ProjectContext';
import { resources } from '../../core/services/resources';
import type { AccessibleProject, ProjectType } from '../../core/types/data-models';

/** Project types this console lists; stock and market projects live in console-exch. */
const CONSOLE_PROJECT_TYPES: ProjectType[] = ['IDEA_VALIDATION', 'BUSINESS_VALIDATION', 'STRATEGY_MANAGEMENT'];

export function toActiveProject(p: AccessibleProject): ActiveProject {
  return {
    id: p.id,
    name: p.name,
    description: p.description,
    tenantId: p.tenantId,
    tenantName: p.tenantName,
    access: p.access,
    role: p.role,
    sharedBy: p.sharedBy,
  };
}

/**
 * Shared behaviour for every place that lists and opens projects.
 *
 * Cross-workspace selection is subtle enough that it must exist in exactly one
 * place: switching tenant re-mints the JWT, which fires ProjectContext's
 * "clear on workspace change" effect and would wipe the selection we are in the
 * middle of making. `beginCrossTenantSelection` suppresses that for one change.
 */
export function useProjectSelection() {
  const { activeTenantId } = useTenant();
  const { switchTenant } = useAuth();
  const { activeProject, setActiveProject, beginCrossTenantSelection } = useActiveProject();
  const [switching, setSwitching] = useState(false);

  const query = useQuery({
    queryKey: ['my-projects'],
    queryFn: () => resources.myProjects(),
  });

  const { own, shared } = useMemo(() => {
    const all = (query.data ?? []).filter((p) => CONSOLE_PROJECT_TYPES.includes(p.projectType));
    return {
      own: all.filter((p) => p.access === 'own'),
      shared: all.filter((p) => p.access === 'shared'),
    };
  }, [query.data]);

  const select = async (project: AccessibleProject): Promise<ActiveProject> => {
    const next = toActiveProject(project);
    if (project.tenantId !== activeTenantId) {
      beginCrossTenantSelection();
      setSwitching(true);
      try {
        await switchTenant(project.tenantId);
      } finally {
        setSwitching(false);
      }
    }
    setActiveProject(next);
    return next;
  };

  return { own, shared, isLoading: query.isLoading, switching, select, activeProject };
}
